// Client Portal API — thin wrappers around the functions-api portal endpoints.
// open/verify/save/submit are PUBLIC (the magic-link token in the body is the
// auth); issue/revoke are AGENT-ONLY and send the signed-in user's Firebase ID
// token as a Bearer header (the backend 401s without it).
//
// Every helper resolves to { ok: true, ...responseJson } on success or
// { ok: false, status?, error } on failure — callers never need try/catch.
import { getAuth } from "firebase/auth";

// Point the portal endpoints at the SAME Firebase project the rest of the app
// is wired to. src/firebase/firebaseConfig.js selects the project off
// NODE_ENV — dev/sandbox → team-henry-demo, production → team-edelstein — so we
// mirror that exact rule here (rather than importing firebaseConfig, which
// would drag its analytics/messaging side effects into this util and its
// tests). The result: the sandbox calls ITS OWN cloud function instead of
// hitting production. team-edelstein is the safe production default.
const PROJECT_ID =
  process.env.NODE_ENV === "development" ? "team-henry-demo" : "team-edelstein";
const API_BASE = `https://us-central1-${PROJECT_ID}.cloudfunctions.net/api`;

async function postPortal(path, body, extraHeaders = {}) {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...extraHeaders },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      return { ok: true, ...data };
    }
    return {
      ok: false,
      status: res.status,
      error: data.error || `HTTP ${res.status}`,
    };
  } catch (err) {
    return { ok: false, error: err?.message || "Network error" };
  }
}

// issue/revoke: attach the Firebase ID token when someone is signed in.
// No user (or a token fetch hiccup) still sends the request — the backend
// answers 401 and the caller shows that error.
async function postPortalAuthed(path, body) {
  const headers = {};
  try {
    const idToken = await getAuth().currentUser?.getIdToken();
    if (idToken) headers.Authorization = `Bearer ${idToken}`;
  } catch {
    /* send without the header; backend will 401 */
  }
  return postPortal(path, body, headers);
}

// Read a File/Blob into a bare base64 string (no "data:...;base64," prefix).
// FileReader.readAsDataURL handles binary safely in the browser and jsdom.
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      const comma = String(result).indexOf(",");
      resolve(comma >= 0 ? String(result).slice(comma + 1) : String(result));
    };
    reader.onerror = () =>
      reject(reader.error || new Error("Could not read that file."));
    reader.readAsDataURL(file);
  });
}

/**
 * Open a portal link: { dealId, token, sessionToken? }.
 * Fresh visit -> { ok, needsVerify: true, brand }; with a still-valid
 * sessionToken -> { ok, needsVerify: false, resume: true, sessionToken,
 * brand, prefill, forms, submitted }.
 */
export function openPortal(body) {
  return postPortal("/portal/open", body);
}

/**
 * Answer the identity question: { dealId, token, answer }.
 * Success -> { ok, sessionToken, prefill, forms, submitted }.
 */
export function verifyPortal(body) {
  return postPortal("/portal/verify", body);
}

/**
 * Autosave a draft: { dealId, token, sessionToken, formId, answers }.
 * answers is a { fieldKey: value } map; a value is string (shortText/longText/
 * single), string[] (multi), or { name, driveFileId?, storagePath? } | null (file).
 */
export function savePortal(body) {
  return postPortal("/portal/save", body);
}

/**
 * Final submit: { dealId, token, sessionToken, formId, answers }.
 * Success -> { ok, pdfDriveFileId }.
 */
export function submitPortal(body) {
  return postPortal("/portal/submit", body);
}

/**
 * Upload a file for a portal form field. The unauthenticated portal can't
 * write Storage directly, so the file rides through the backend as base64:
 * { dealId, token, sessionToken, formId, fieldKey, filename, contentBase64 }.
 * Success -> { ok, name, driveFileId?, storagePath? }.
 * Rejects client-side (no request) when the file is over 10 MB.
 */
export async function uploadPortalFile({
  dealId,
  token,
  sessionToken,
  formId,
  fieldKey,
  file,
}) {
  if (file && file.size > 10 * 1024 * 1024) {
    return { ok: false, error: "That file is over 10 MB." };
  }
  let contentBase64;
  try {
    contentBase64 = await fileToBase64(file);
  } catch (err) {
    return { ok: false, error: err?.message || "Could not read that file." };
  }
  return postPortal("/portal/upload", {
    dealId,
    token,
    sessionToken,
    formId,
    fieldKey,
    filename: file.name,
    contentBase64,
  });
}

/**
 * Agent-only — mint a portal link for a client: { dealId, contactId }.
 * Success -> { ok, token, expiresAt, url }.
 */
export function issuePortal(body) {
  return postPortalAuthed("/portal/issue", body);
}

/**
 * Agent-only — kill a portal link: { token }.
 */
export function revokePortal(body) {
  return postPortalAuthed("/portal/revoke", body);
}

// --- dev/demo fallback ------------------------------------------------------
// The agent-only issue/revoke endpoints aren't deployed in the sandbox, so the
// agent panel degrades to a fully local demo (mirrors the seller page's
// devLocal preview): issue/revoke/send succeed with no backend, and Open lands
// on the seller page, which runs its own devLocal.
export const IS_DEV = process.env.NODE_ENV === "development";

// Build the local portal URL a demo link points at (HashRouter: /#/portal?…).
// window.location.pathname keeps it working whether the app is served from "/"
// or a sub-path.
export function buildPortalUrl(dealId, token) {
  const base =
    typeof window !== "undefined" && window.location
      ? `${window.location.origin}${window.location.pathname}`
      : "";
  return `${base}#/portal?d=${encodeURIComponent(dealId)}&t=${encodeURIComponent(
    token,
  )}`;
}

// A synthetic active-link summary for the sandbox — same shape /portal/issue
// returns, so the panel flips to its active-link state and Copy/Open work.
export function makeDemoPortal(dealId) {
  const token = `demo-${Math.random().toString(36).slice(2, 10)}`;
  return {
    ok: true,
    demo: true,
    token,
    url: buildPortalUrl(dealId, token),
    // Same one-year horizon the backend uses.
    expiresAt: Date.now() + 365 * 24 * 60 * 60 * 1000,
  };
}

// Turn a failed portal response into a client-facing message. The agent should
// never see a raw "HTTP 404"/"HTTP 500": a missing/erroring endpoint means the
// service isn't live yet. Used in production only — dev uses demo mode.
export function friendlyPortalError(res) {
  const status = res?.status;
  const raw = res?.error || "";
  const looksUndeployed =
    status === undefined || // network throw (no response at all)
    status === 404 ||
    status === 501 ||
    status >= 500 ||
    /^HTTP \d+$/.test(raw); // bare status text from postPortal
  if (looksUndeployed) {
    return "The portal service isn’t available yet — it goes live after the backend deploy.";
  }
  // A real, specific backend message (e.g. a 400/401/403) — surface it as-is.
  return raw || "Something went wrong with the portal link. Please try again.";
}
